/** Resolve a work-report image payload to a displayable src. */
export function imageSrc(image, fallback) {
  const raw =
    (typeof image === 'string' ? image : null) ||
    image?.dataUrl ||
    image?.url ||
    fallback;
  if (typeof raw !== 'string' || !raw.trim()) return null;
  const src = raw.trim();
  if (
    src.startsWith('data:') ||
    src.startsWith('blob:') ||
    src.startsWith('http://') ||
    src.startsWith('https://')
  ) {
    return src;
  }
  return null;
}

export function parseNotes(json) {
  try {
    const parsed = JSON.parse(json || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return {};
  }
}

/**
 * Before/after shots from approved work reports — the same evidence the
 * client portal can surface. Accepts data URLs and hosted http(s) URLs.
 */
function parsePhotos(json) {
  try {
    const parsed = JSON.parse(json || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Photos the cleaner took of the resources / supplies they used. */
export function collectResourcePhotos(report) {
  const stored = parsePhotos(report.photosJson).filter(
    (photo) => photo?.category === 'resource' && photo.url,
  );
  return stored.map((photo, index) => ({
    id: `${report.id}-res-${photo.key || index}`,
    key: photo.key,
    src: photo.url,
    label: photo.name || `Resource ${index + 1}`,
  }));
}

export function collectVisitPhotos(report) {
  const notes = parseNotes(report.notesJson);
  const manual = notes.manualReport || {};
  const out = [];

  // Photos stored on the linked work report — the cleaner's uploads plus any
  // the office added — the same set the cleaner portal shows.
  const stored = parsePhotos(report.photosJson).filter(
    (photo) => photo?.category !== 'resource',
  );
  if (stored.length) {
    stored.forEach((photo, index) => {
      if (!photo?.url) return;
      const kind = photo.category === 'after' ? 'After' : 'Before';
      const office = photo.source === 'office';
      out.push({
        id: `${report.id}-${photo.key || index}`,
        reportId: report.id,
        workReportId: report.workReportId,
        key: photo.key,
        source: office ? 'office' : 'cleaner',
        addedByName: photo.addedByName || '',
        date: report.date,
        cleanerId: report.userId || report.employeeName || 'unknown',
        cleanerName: report.employeeName || 'Cleaner',
        area: '',
        label: String(photo.name || '').replace(/\.[a-z0-9]+$/i, '') || kind,
        kind,
        src: photo.url,
        downloadUrl: photo.downloadUrl || '',
        fileName: photo.name || '',
        contentType: photo.contentType || '',
      });
    });
    return out;
  }

  const push = (image, name, kind) => {
    const src = imageSrc(image);
    if (!src) return;
    if (out.some((row) => row.src === src && row.kind === kind)) return;
    out.push({
      id: `${report.id}-${kind}-${out.length}`,
      reportId: report.id,
      date: report.date,
      cleanerId: report.userId || report.employeeName || 'unknown',
      cleanerName: report.employeeName || 'Cleaner',
      // Room/area tag when the uploader supplied one (e.g. "Bathroom").
      area:
        (image && typeof image === 'object'
          ? image.area || image.room || image.areaName || image.location
          : '') || '',
      label: String(name || '').replace(/\.[a-z0-9]+$/i, '') || kind,
      kind,
      src,
    });
  };

  push(notes.beforePictureDataUrl, notes.beforePictureName, 'Before');
  push(notes.afterPictureDataUrl, notes.afterPictureName, 'After');

  [notes, manual].forEach((source) => {
    ['beforePictures', 'afterPictures'].forEach((key) => {
      const kind = key === 'beforePictures' ? 'Before' : 'After';
      const arr = source?.[key];
      if (Array.isArray(arr)) {
        arr.forEach((image, index) =>
          push(image, image?.name || `${kind} ${index + 1}`, kind),
        );
      }
    });
  });

  return out;
}
